/**
 * Shared pure helpers extracted from App.js.
 * No React state — safe to import anywhere.
 */
import React from 'react';
import logger from '../utils/logger';
import {
  getEnhancedCompanyIcon,
  getMediaChannelIcon,
} from '../components/EnhancedLogoSystem';

// -------------------------------------------------------------------
// Backend URL resolution
// -------------------------------------------------------------------
export const getBackendUrl = () => {
  const hostname = window.location.hostname;
  if (hostname.includes('preview.emergentagent.com')) return window.location.origin;
  if (
    hostname === 'www.bcmventas.com' ||
    hostname === 'bcmventas.com' ||
    hostname === 'marketvision-20.emergent.host'
  ) {
    return `https://${hostname}`;
  }
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

export const BACKEND_URL = getBackendUrl();
export const API = `${BACKEND_URL}/api`;

logger.info('Backend URL:', BACKEND_URL);

// -------------------------------------------------------------------
// Text rendering
// -------------------------------------------------------------------
export const renderBoldText = (text) => {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

// -------------------------------------------------------------------
// Brand icon helpers (delegate to EnhancedLogoSystem)
// -------------------------------------------------------------------
export const getCompanyIcon = (companyName) => getEnhancedCompanyIcon(companyName, 24);
export const getCompanyLogo = (companyName) => getEnhancedCompanyIcon(companyName, 32);
export const getMediaIcon   = (channelName) => getMediaChannelIcon(channelName);

// -------------------------------------------------------------------
// Time formatting
// -------------------------------------------------------------------
export const formatTimeRemaining = (seconds) => {
  if (seconds <= 0) return '';
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.ceil(seconds / 60);
  if (minutes === 1) return '1 minute';
  if (minutes <= 5) return `${minutes} minutes`;
  const minMinutes = Math.floor(minutes * 0.8);
  const maxMinutes = Math.ceil(minutes * 1.2);
  return `${minMinutes}-${maxMinutes} minutes`;
};

// -------------------------------------------------------------------
// Currency formatting (used in results)
// -------------------------------------------------------------------
export const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '$0';
  if (value >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
  if (value >= 1e9)  return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6)  return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3)  return `$${(value / 1e3).toFixed(1)}K`;
  return `$${value.toLocaleString()}`;
};

// -------------------------------------------------------------------
// Step icons for the analysis-progress modal
// -------------------------------------------------------------------
export const getStepIcon = (stepIndex, isActive) => {
  const iconClass = `w-4 h-4 ${isActive ? 'text-white' : 'text-gray-400'}`;
  switch (stepIndex) {
    case 0:
      return (
        <svg className={`${iconClass} ${isActive ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      );
    case 1:
      return (
        <svg className={`${iconClass} ${isActive ? 'animate-pulse' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          {isActive && (
            <circle className="animate-ping" cx="11" cy="11" r="3" fill="currentColor" opacity="0.3" />
          )}
        </svg>
      );
    case 2:
      return (
        <svg className={`${iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          {isActive && (
            <g className="animate-bounce">
              <circle cx="12" cy="7" r="1" fill="currentColor" />
              <circle cx="19" cy="10" r="0.5" fill="currentColor" />
              <circle cx="5" cy="10" r="0.5" fill="currentColor" />
            </g>
          )}
        </svg>
      );
    case 3:
      return (
        <svg className={`${iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          {isActive && (
            <g className="animate-pulse">
              <rect x="9" y="12" width="6" height="0.5" fill="currentColor" opacity="0.6" />
              <rect x="9" y="16" width="6" height="0.5" fill="currentColor" opacity="0.6" />
            </g>
          )}
        </svg>
      );
    case 4:
      return (
        <svg className={`${iconClass} ${isActive ? 'animate-pulse' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      );
    default:
      return <div className="w-4 h-4 bg-current rounded-full"></div>;
  }
};
